import { useCallback, useRef } from 'react';

import { ApolloError, ApolloQueryResult, useApolloClient } from '@apollo/client';
import { mapAdapterEntriesToServiceEntries } from 'features/promotions/utils/map-best-promotion-input';

import {
  ILoyaltyPromotionInput,
  ILoyaltyServiceRewardsQuery,
  ILoyaltyServiceRewardsQueryVariables,
  LoyaltyServicePromotionType,
  LoyaltyServiceRewardsDocument,
} from 'generated/graphql-gateway';
import { actions, useAppDispatch } from 'state/global-state';
import {
  ICartEntryAdapter,
  IIncentiveEvaluationResult,
  IncentiveEvaluationErrorCodes,
  IncentiveEvaluationMap,
} from 'state/loyalty/hooks/types';
import { logger } from 'utils/logger';

import { getLoyaltyServiceRewardServiceMode } from '../utils/service-mode';

type LoyaltyServiceRewardCategories = NonNullable<
  ILoyaltyServiceRewardsQuery['loyaltyServiceRewards']
>['categories'];

const REWARD_NOT_AVAILABLE_EVALUATION_RESULT: IIncentiveEvaluationResult = {
  code: IncentiveEvaluationErrorCodes.REWARD_NOT_AVAILABLE,
  currentValue: null,
  message: '',
  ruleId: '',
  targetValue: null,
};

/**
 * Builds the map the cart reads to show what's wrong with an applied reward.
 * Loyalty-service version of the older `createFeedbackMaps` for rewards.
 *
 * Keyed by the reward `id` — the engine id the cart looks a reward up by
 * (`referenceId` / `rewardIdReference`). Only rewards with a non-empty `errors[]`
 * produce an entry; a clean reward produces none (matching the legacy path, where
 * `Object.values(rewardsFeedbackMap).length` gates checkout).
 *
 * `locked` / `remainingPointsNeededForRedemption` are intentionally not mapped
 * into `errors[]`: the service defers hard point enforcement to transaction
 * creation, so translating them here would invent a client-side block.
 *
 * Unlike offers, there is no ON-path reward eviction, so an applied reward id
 * missing from the response is surfaced as a synthetic `REWARD_NOT_AVAILABLE`
 * entry keyed by that applied id to preserve continue-blocking and price-order
 * sanitization.
 */
export const createServiceRewardFeedbackMap = (
  categories?: LoyaltyServiceRewardCategories | null,
  appliedRewardIds: readonly string[] = []
): IncentiveEvaluationMap => {
  const feedbackMap: IncentiveEvaluationMap = {};
  const appliedIds = new Set(appliedRewardIds.filter(Boolean));
  const presentIds = new Set<string>();

  for (const category of categories ?? []) {
    for (const reward of category.rewards) {
      // Only trust feedback for rewards the user actually applied. The service
      // may return sibling catalog rewards; mapping their errors here would
      // wrongly block continue (cart-content blocks on any rewardsFeedbackMap entry).
      if (!appliedIds.has(reward.id)) {
        continue;
      }
      presentIds.add(reward.id);
      if (reward.errors?.length) {
        feedbackMap[reward.id] = reward.errors.map(
          ({ code, currentValue, message, ruleId, targetValue }) => ({
            code,
            currentValue,
            message,
            ruleId,
            targetValue,
          })
        );
      }
    }
  }

  for (const id of appliedIds) {
    if (!presentIds.has(id)) {
      feedbackMap[id] = [REWARD_NOT_AVAILABLE_EVALUATION_RESULT];
    }
  }

  return feedbackMap;
};

export interface EvaluateLoyaltyServiceRewardsParams {
  /** Rewards the user has applied to their cart. */
  appliedIncentives: ILoyaltyPromotionInput[];
  /** The items currently in the cart. */
  cartEntries?: ICartEntryAdapter[];
  serviceMode?: string | null;
  storeId?: string | null;
}

/**
 * Checks the rewards a user has applied to their cart against the loyalty service
 * and returns what (if anything) is wrong with each one, in the shape the cart
 * already knows how to display. Loyalty-service version of `evaluateLoyaltyRewards`.
 *
 * Only meant for signed-in users: the caller skips it when there is no loyalty
 * user, and the anonymous rewards query does not accept `appliedIncentives` /
 * `cartEntries`, so cart reward validation stays authenticated-only.
 */
export const useEvaluateLoyaltyServiceRewards = () => {
  const client = useApolloClient();
  const dispatch = useAppDispatch();
  // Guards against out-of-order service responses clobbering a newer feedback map.
  const latestEvaluationRef = useRef(0);

  const evaluateLoyaltyServiceRewards = useCallback(
    async ({
      appliedIncentives,
      cartEntries,
      serviceMode,
      storeId,
    }: EvaluateLoyaltyServiceRewardsParams): Promise<IncentiveEvaluationMap | undefined> => {
      // Drop blank ids: the service validates the `rewardIds` batch and can
      // reject blanks, failing the whole query.
      const serviceAppliedIncentives = appliedIncentives
        .filter(({ id }) => id)
        .map(({ id, lineId }) => ({
          id,
          lineId,
          type: LoyaltyServicePromotionType.REWARD,
        }));

      // Nothing to validate: return undefined so existing feedback isn't wiped.
      if (serviceAppliedIncentives.length === 0) {
        return undefined;
      }

      try {
        const { data, errors }: ApolloQueryResult<ILoyaltyServiceRewardsQuery> = await client.query<
          ILoyaltyServiceRewardsQuery,
          ILoyaltyServiceRewardsQueryVariables
        >({
          query: LoyaltyServiceRewardsDocument,
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
          variables: {
            rewardIds: serviceAppliedIncentives.map(({ id }) => id),
            appliedIncentives: serviceAppliedIncentives,
            cartEntries: mapAdapterEntriesToServiceEntries(cartEntries ?? []),
            serviceMode: getLoyaltyServiceRewardServiceMode(serviceMode),
            storeId: storeId ?? undefined,
            omitInvalids: false,
          },
        });

        if (errors?.length) {
          throw new ApolloError({ graphQLErrors: [...errors] });
        }

        const categories = data?.loyaltyServiceRewards?.categories;
        // If the request comes back with nothing (e.g. it failed), leave the
        // cart's current reward messages as-is instead of clearing them.
        if (!categories) {
          return undefined;
        }

        return createServiceRewardFeedbackMap(
          categories,
          serviceAppliedIncentives.map(({ id }) => id)
        );
      } catch (e) {
        logger.error(`Loyalty service rewards evaluation error: ${e}`);
        throw e;
      }
    },
    [client]
  );

  /**
   * Evaluates the applied rewards and applies the result to the cart's feedback
   * map, guarding against out-of-order responses. This is the shared result
   * handling all cart seams rely on (stale-guard + dispatch).
   *
   * An `undefined` result (blank ids / no categories / failed request)
   * intentionally leaves the current feedback untouched instead of clearing it.
   */
  const evaluateAndApplyRewardsFeedback = useCallback(
    async (params: EvaluateLoyaltyServiceRewardsParams): Promise<void> => {
      const evaluationId = ++latestEvaluationRef.current;
      const result = await evaluateLoyaltyServiceRewards(params);
      if (result && evaluationId === latestEvaluationRef.current) {
        dispatch(actions.loyalty.setRewardsFeedbackMap(result));
      }
    },
    [evaluateLoyaltyServiceRewards, dispatch]
  );

  /**
   * Treats an empty applied-reward set as the newest evaluation state. Advancing
   * the instance-local generation prevents an older in-flight response from
   * restoring feedback after the final reward has been removed.
   */
  const clearRewardsFeedback = useCallback(() => {
    latestEvaluationRef.current++;
    dispatch(actions.loyalty.setRewardsFeedbackMap({}));
  }, [dispatch]);

  return {
    evaluateLoyaltyServiceRewards,
    evaluateAndApplyRewardsFeedback,
    clearRewardsFeedback,
  };
};
